export const CHECKOUT_URL = 'https://trt-it.impultienda.ar/checkout/'

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/aXaCatgDEmyywGxHxcMy6r/TRT-IT.png'

export const stats = [
  { value: '6', label: 'Volumi clinici' },
  { value: '300+', label: 'Protocolli clinici' },
  { value: '10', label: 'Algoritmi decisionali' },
  { value: '30+', label: 'Casi clinici risolti' },
]

export const benefits = [
  {
    title: 'Sapere quando la TRT deve — e non deve — essere presa in considerazione',
    text: 'Smetti di basarti su un singolo valore di testosterone. Segui un percorso diagnostico strutturato che considera il quadro clinico, i risultati biochimici e le possibili cause sottostanti.',
  },
  {
    title: 'Interpretare profili ormonali complessi con maggiore chiarezza',
    text: 'Comprendi il ruolo del testosterone totale, del testosterone libero, dell’SHBG, dell’albumina, dell’LH, dell’FSH, della prolattina e dell’estradiolo nel contesto clinico completo.',
  },
  {
    title: 'Affrontare le domande cliniche più difficili con un sistema strutturato',
    text: 'Percorsi clinici organizzati per fertilità, salute prostatica, rischio cardiovascolare e monitoraggio del trattamento.',
  },
  {
    title: 'Sapere cosa monitorare dopo l’inizio del trattamento',
    text: 'Un approccio strutturato al monitoraggio degli esami, della risposta al trattamento, degli aggiustamenti della dose, degli eventi avversi e della rivalutazione terapeutica.',
  },
  {
    title: 'Smettere di improvvisare il tuo processo clinico',
    text: 'Invece di passare da linee guida ad articoli e appunti sparsi, avrai il tuo flusso decisionale sulla TRT organizzato in un unico posto.',
  },
]

export const withoutSystem = [
  'Più linee guida aperte contemporaneamente',
  'Ore trascorse a cercare nella letteratura medica',
  'Dubbi sul fatto che il paziente soddisfi i criteri',
  'Profili ormonali complessi che richiedono ricerche continue',
  'Nessuna sequenza chiara dalla diagnosi al trattamento',
  'Incertezza su cosa monitorare e quando',
]

export const withSystem = [
  'Un percorso diagnostico strutturato',
  'Protocolli clinici organizzati per scenario',
  'Algoritmi decisionali per le domande più comuni',
  'Guide visive per l’interpretazione degli esami',
  'Percorsi per il trattamento e il monitoraggio',
  'Una risorsa pratica da consultare quando ti serve',
]

export const painPoints = [
  {
    title: 'Non eri completamente sicuro che un paziente soddisfacesse davvero i criteri per la TRT.',
    text: 'Sapevi che esistevano delle linee guida — ma trovare esattamente l’informazione di cui avevi bisogno durante una visita intensa era un’altra storia.',
  },
  {
    title: 'Hai trascorso troppo tempo a leggere articoli prima di prendere una decisione.',
    text: 'Una domanda portava a un altro articolo, poi a un’altra linea guida e infine a un’altra ricerca.',
  },
  {
    title: 'I risultati di laboratorio non raccontavano una storia semplice.',
    text: 'Testosterone, SHBG, testosterone libero, LH, FSH, prolattina… e una visita semplice diventava una sessione di ricerca.',
  },
  {
    title: 'Non eri completamente sicuro del piano di monitoraggio.',
    text: 'Cosa controllare? Quando controllarlo? Quando il trattamento deve essere rivalutato?',
  },
  {
    title: 'Un paziente ti ha posto una domanda difficile su fertilità, prostata o rischio cardiovascolare.',
    text: 'E volevi una risposta strutturata, basata sulle evidenze e clinicamente responsabile.',
  },
  {
    title: 'Avresti voluto un sistema pratico invece di centinaia di pagine da consultare.',
    text: 'Perché conoscere l’informazione non è la stessa cosa che trovarla nel momento in cui ti serve.',
  },
]

export type Volume = {
  number: string
  title: string
  intro: string
  listLabel: string
  items: string[]
  goal?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume I',
    title: 'Il metodo per formulare la diagnosi in modo più accurato',
    intro:
      'Costruisci un approccio strutturato per identificare i pazienti che possono essere candidati appropriati alla TRT — riconoscendo le situazioni in cui serve un’ulteriore valutazione.',
    listLabel: 'Esplorerai',
    items: [
      'Valutazione clinica',
      'Conferma biochimica',
      'Ipogonadismo primario e secondario',
      'Diagnosi differenziale',
      'Cause reversibili',
      'Percorsi decisionali diagnostici',
    ],
  },
  {
    number: 'Volume II',
    title: 'Domina l’interpretazione avanzata dei profili ormonali',
    intro:
      'Trasforma pannelli ormonali complessi in una valutazione clinica più strutturata.',
    listLabel: 'Imparerai ad analizzare',
    items: [
      'Testosterone totale',
      'Testosterone libero',
      'SHBG',
      'Albumina',
      'LH e FSH',
      'Prolattina',
      'Estradiolo',
      'Pattern ormonali e contesto clinico',
    ],
    goal:
      'Smettere di osservare numeri isolati e iniziare a interpretare il quadro completo.',
  },
  {
    number: 'Volume III',
    title: 'Costruisci una strategia terapeutica più intelligente',
    intro:
      'Comprendi le principali formulazioni di testosterone e come la scelta terapeutica si inserisce nel profilo clinico del paziente.',
    listLabel: 'Esplora',
    items: [
      'Formulazioni iniettabili',
      'Formulazioni a lunga durata d’azione',
      'Opzioni transdermiche',
      'Principi farmacocinetici',
      'Selezione del trattamento',
      'Aggiustamenti terapeutici',
      'Considerazioni sul follow-up',
    ],
  },
  {
    number: 'Volume IV',
    title: 'Proteggi la fertilità e l’equilibrio ormonale',
    intro:
      'Affronta una delle aree più complesse della terapia ormonale maschile con un percorso clinico più strutturato.',
    listLabel: 'Include',
    items: [
      'Considerazioni sulla fertilità',
      'hCG',
      'SERM',
      'Clomifene',
      'Soppressione ormonale',
      'Strategie di preservazione della fertilità',
      'Percorsi decisionali clinici',
    ],
  },
  {
    number: 'Volume V',
    title: 'Affronta i pazienti che non rientrano nello schema standard',
    intro:
      'Costruisci un approccio più organizzato agli scenari clinici complessi che coinvolgono:',
    listLabel: 'Scenari coperti',
    items: [
      'Obesità',
      'Diabete di tipo 2',
      'Sindrome metabolica',
      'Considerazioni cardiovascolari',
      'Pazienti anziani',
      'Popolazioni cliniche speciali',
      'Valutazione del rischio',
    ],
  },
  {
    number: 'Volume VI',
    title: 'Monitora la TRT come un professionista',
    intro: 'Perché iniziare il trattamento è solo l’inizio.',
    listLabel: 'Struttura il monitoraggio continuo attorno a',
    items: [
      'Risposta al trattamento',
      'Livelli di testosterone',
      'Ematocrito',
      'PSA',
      'Estradiolo',
      'Eventi avversi',
      'Rivalutazione della dose',
      'Follow-up a lungo termine',
    ],
  },
]

export const extras = [
  {
    title: '300+ Protocolli clinici',
    text: 'Percorsi strutturati che coprono le fasi e gli scenari più importanti dell’intero processo TRT.',
    icon: 'protocol',
  },
  {
    title: 'Algoritmi decisionali clinici',
    text: 'Alberi decisionali visivi: domanda clinica → valutazione → decisione → prossimo passo.',
    icon: 'algorithm',
  },
  {
    title: 'Guide all’interpretazione ormonale',
    text: 'Riferimenti visivi pratici per testosterone, SHBG, testosterone libero, LH, FSH, prolattina ed estradiolo.',
    icon: 'lab',
  },
  {
    title: 'Tabelle per la scelta del trattamento',
    text: 'Confronti organizzati per comprendere le principali formulazioni TRT e le relative considerazioni.',
    icon: 'table',
  },
  {
    title: 'Protocolli per scenari speciali',
    text: 'Approcci strutturati per obesità, diabete, rischio cardiovascolare, fertilità e altri casi complessi.',
    icon: 'special',
  },
  {
    title: '30+ Casi clinici risolti',
    text: 'Dalla presentazione clinica e dai dati di laboratorio fino al processo decisionale, passo dopo passo.',
    icon: 'cases',
  },
]

export type Bonus = {
  index: string
  value: string
  title: string
  subtitle: string
  text: string
  listLabel: string
  items: string[]
  image: string
  imageAlt: string
}

export const bonuses: Bonus[] = [
  {
    index: 'Bonus #1',
    value: '49 EUR',
    title: 'Pack di Algoritmi Clinici per la TRT™',
    subtitle: '10 Algoritmi decisionali stampabili',
    text: 'Una scorciatoia visiva per le situazioni cliniche che incontri più spesso.',
    image: 'https://cdn.magicpatterns.com/uploads/xrfSaVDRpRNbZnFWVW2nVT/BONUS_1_TRT-IT.png',
    imageAlt:
      'Bonus #1 — TRT Clinical Algorithm Pack™: 10 algoritmi decisionali stampabili su tablet, smartphone e schede cliniche',
    listLabel: 'Include algoritmi strutturati per',
    items: [
      'Valutazione diagnostica',
      'Candidatura alla TRT',
      'Controindicazioni',
      'Aggiustamento del trattamento',
      'Ematocrito',
      'PSA',
      'Estradiolo',
      'Fertilità',
      'Monitoraggio',
      'Rivalutazione del trattamento',
    ],
  },
  {
    index: 'Bonus #2',
    value: '97 EUR',
    title: 'Biblioteca di Casi Clinici Risolti sulla TRT™',
    subtitle: '30+ Casi clinici analizzati passo dopo passo',
    text: 'Scopri come organizzare il ragionamento quando ti trovi davanti a un caso reale.',
    image: 'https://cdn.magicpatterns.com/uploads/xdJbxFVAXNzewg9ELb9QY9/BONUS_2_TRT-IT.png',
    imageAlt:
      'Bonus #2 — Libreria di Casi Clinici TRT™: 30+ casi clinici risolti passo dopo passo su tablet, smartphone e libro',
    listLabel: 'Ogni caso include',
    items: [
      'Presentazione clinica',
      'Anamnesi rilevante',
      'Risultati di laboratorio',
      'Interpretazione clinica',
      'Considerazioni differenziali',
      'Percorso decisionale',
      'Considerazioni gestionali',
      'Follow-up',
      'Punti chiave clinici',
    ],
  },
  {
    index: 'Bonus #3',
    value: '67 EUR',
    title: 'Kit Professionale per la Visita TRT™',
    subtitle: '8 Strumenti clinici pronti all’uso',
    text: 'Modelli progettati per organizzare la visita e il processo di follow-up.',
    image: 'https://cdn.magicpatterns.com/uploads/ka3cij8iFTJborD7wSrggC/BONUS_3_TRT-IT.png',
    imageAlt:
      'Bonus #3 — TRT Professional Consultation Kit™: 8 strumenti clinici pratici, modelli e schede di monitoraggio',
    listLabel: 'Include',
    items: [
      'Modello di anamnesi specifico per la TRT',
      'Modello per la richiesta iniziale degli esami',
      'Scheda di monitoraggio',
      'Registro delle dosi',
      'Scheda di monitoraggio dei sintomi',
      'Checklist di follow-up',
      'Cronologia del monitoraggio',
      'Risorse educative per il paziente',
    ],
  },
  {
    index: 'Bonus #4',
    value: '79 EUR',
    title: 'Calcolatore Clinico per la TRT™',
    subtitle: 'Rendi più rapidi i calcoli clinici più comuni',
    text: 'Uno strumento pratico basato su fogli di calcolo per organizzare calcoli e monitoraggio.',
    image: 'https://cdn.magicpatterns.com/uploads/bRma6yhFR1UKQS4nf3x2zx/BONUS_4_TRT-IT.png',
    imageAlt:
      'Bonus #4 — TRT Clinical Calculator™: foglio di calcolo modificabile per testosterone libero, BMI, conversioni e follow-up',
    listLabel: 'Include strumenti per',
    items: [
      'Calcolo del testosterone libero',
      'BMI',
      'Conversione delle unità',
      'Intervalli di trattamento',
      'Date di follow-up',
      'Monitoraggio degli esami',
      'Monitoraggio longitudinale del paziente',
    ],
  },
  {
    index: 'Bonus #5',
    value: '57 EUR',
    title: 'Guida Rapida alle Complicanze della TRT™',
    subtitle: 'Cosa valutare quando il caso non va come previsto',
    text: 'Una guida di consultazione rapida sui principali scenari di gestione della TRT.',
    image: 'https://cdn.magicpatterns.com/uploads/jWGpjJTfonypirKVz8P94X/BONUS_5_TRT-IT.png',
    imageAlt:
      'Bonus #5 — TRT Complications & Problem-Solving Quick Guide™: guida rapida agli scenari clinici più comuni',
    listLabel: 'Include',
    items: [
      'Ematocrito elevato',
      'Variazioni del PSA',
      'Problematiche legate all’estradiolo',
      'Ginecomastia',
      'Problemi di fertilità',
      'Mancata risposta attesa',
      'Eventi avversi comuni',
      'Rivalutazione del trattamento',
    ],
  },
]

export const valueStack = [
  { name: 'Sistema Maestro della TRT™ — 6 Volumi Clinici', value: '197 EUR' },
  { name: 'Pack di Algoritmi Clinici per la TRT™', value: '49 EUR' },
  { name: 'Biblioteca di Casi Clinici Risolti™', value: '97 EUR' },
  { name: 'Kit Professionale per la Visita TRT™', value: '67 EUR' },
  { name: 'Calcolatore Clinico per la TRT™', value: '79 EUR' },
  { name: 'Guida Rapida alle Complicanze della TRT™', value: '57 EUR' },
]

export const includes = [
  { title: '6 Volumi clinici', text: 'Un percorso completo dalla valutazione diagnostica al monitoraggio a lungo termine.' },
  { title: '300+ Protocolli clinici', text: 'Riferimenti strutturati organizzati intorno alle decisioni TRT più comuni.' },
  { title: 'Algoritmi decisionali', text: 'Percorsi visivi progettati per una consultazione più rapida.' },
  { title: '30+ Casi risolti', text: 'Scopri come il ragionamento clinico può essere applicato a diversi scenari.' },
  { title: '5 Bonus pratici', text: 'Strumenti progettati per rendere più semplice l’applicazione e il follow-up.' },
  { title: 'Accesso digitale', text: 'Utilizza il materiale da computer, tablet o smartphone.' },
  { title: 'Accesso a vita', text: 'Un unico pagamento con accesso continuativo al materiale.' },
  { title: 'Aggiornamenti futuri', text: 'Quando applicabile, le versioni aggiornate arrivano nello stesso canale digitale.' },
]

export const testimonials = [
  {
    quote:
      'Prima aprivo diverse linee guida per ogni consulenza sulla TRT. Ora apro un algoritmo e capisco più rapidamente quale percorso valutare.',
    name: 'Dott. Andrés Villalba',
    role: 'Medicina di famiglia · Bogotá',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/3afd36ca-310b-4b7e-bad1-0584b7eed29c.jpg',
  },
  {
    quote:
      'Il volume sull’interpretazione ormonale ha cambiato il modo in cui leggo un pannello. SHBG e testosterone libero sono finalmente in un contesto clinico completo.',
    name: 'Dott.ssa Mariana Cortés',
    role: 'Endocrinologia · Monterrey',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/761db3e3-95ff-4400-a3ef-93bec3270d68.jpg',
  },
  {
    quote:
      'La libreria dei casi risolti è la parte che non mi aspettavo di usare così tanto. Vedere il ragionamento passo dopo passo ha reso il mio processo molto più chiaro.',
    name: 'Dott. Rafael Ibarra',
    role: 'Urologia · Santiago',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/ef9e2c53-f400-499c-bef3-dfe89246703a.jpg',
  },
  {
    quote:
      'Le schede di monitoraggio e la checklist di follow-up mi hanno aiutato a strutturare molto meglio il processo di controllo.',
    name: 'Dott. Julián Restrepo',
    role: 'Medicina interna · Lima',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/da9cdce6-95b9-49a9-b10a-ab8ea6ad2655.jpg',
  },
]

export const fitYes = [
  'Organizzare il tuo processo decisionale nella TRT',
  'Rivedere i concetti clinici in modo più efficiente',
  'Avere algoritmi visivi disponibili per la consultazione',
  'Analizzare casi clinici rappresentativi',
  'Strutturare il tuo processo di monitoraggio',
  'Tenere le risorse pratiche sulla TRT in un unico posto',
]

export const fitNo = [
  'Sostituire il giudizio clinico',
  'Sostituire le linee guida aggiornate o le informazioni ufficiali sulla prescrizione',
  'Sostituire la consulenza specialistica quando appropriata',
  'Fornire consigli medici personalizzati per un paziente specifico',
]

export const faqs = [
  {
    q: 'Come riceverò il Sistema Maestro della TRT™?',
    a: 'Subito dopo l’acquisto riceverai le istruzioni per l’accesso digitale. Il materiale può essere consultato da computer, tablet o smartphone.',
  },
  { q: 'È un prodotto fisico?', a: 'No. È un prodotto digitale con accesso immediato dopo l’acquisto.' },
  { q: 'È un corso?', a: 'No. È un sistema di riferimento clinico digitale progettato per una consultazione pratica e organizzata.' },
  {
    q: 'A chi è destinato?',
    a: 'È una risorsa educativa per professionisti sanitari qualificati interessati a strutturare meglio il proprio processo di revisione e decisione clinica relativo alla TRT.',
  },
  {
    q: 'Il contenuto è basato su evidenze scientifiche?',
    a: 'Il materiale organizza informazioni provenienti da fonti cliniche riconosciute e dalla letteratura scientifica. Deve essere utilizzato insieme al giudizio clinico e alle fonti più aggiornate.',
  },
  { q: 'Posso utilizzarlo su smartphone o tablet?', a: 'Sì. Il materiale digitale può essere consultato da computer, tablet o smartphone.' },
  { q: 'È un abbonamento?', a: 'No. Paghi una sola volta per l’accesso digitale al sistema.' },
  { q: 'Per quanto tempo avrò accesso?', a: 'Ricevi accesso continuativo al materiale digitale secondo le condizioni dell’offerta.' },
]

export const buyerNotifications = [
  { name: 'Dott. Alejandro Ruiz', place: 'Madrid, Spagna' },
  { name: 'Dott.ssa Camila Herrera', place: 'Bogotá, Colombia' },
  { name: 'Dott. Sergio Molina', place: 'Città del Messico, Messico' },
  { name: 'Dott.ssa Valentina Ortiz', place: 'Buenos Aires, Argentina' },
  { name: 'Dott. Ricardo Peña', place: 'Lima, Perù' },
  { name: 'Dott.ssa Lucía Fernández', place: 'Barcellona, Spagna' },
  { name: 'Dott. Mateo Salazar', place: 'Santiago, Cile' },
  { name: 'Dott.ssa Andrea Vargas', place: 'Quito, Ecuador' },
  { name: 'Dott. Emilio Castaño', place: 'Montevideo, Uruguay' },
  { name: 'Dott.ssa Paola Guzmán', place: 'San José, Costa Rica' },
  { name: 'Dott. Iván Delgado', place: 'Asunción, Paraguay' },
  { name: 'Dott.ssa Natalia Rivas', place: 'Panama City, Panama' },
]
